import { sampleNoise } from "../war/warNoiseData";

/*
    The snow caps' sums without Pixi (0.98.8: moved here from managers/snowLook.ts as they were, for the bakes' worker,
    managers/atlasReadWorker.ts, which does them away from the frame; snowLook.ts does them itself where there's no
    worker): a cap from its picture's pixels, the picture's shape eroded in from its edge, under patches of snow laid by
    noise, white, bluer in its shadows, lit from the top left.
*/

/** the cap's look: how far in from the picture's edge the snow keeps (a share of the picture's size), how much of the top it covers, how white */
export const CAP = Object.freeze({
    erode: 0.075,
    cover: { from: 0.44, to: 0.6 },
    alpha: 0.92,
    /** the baked picture's longest side (pixels): the cap is soft, drawn small and stretched (a roof's bigger: it fills the screen) */
    size: 224,
    roofSize: 384,
    /** a frame bakes caps until this much of it is spent (one at least): a town's worth of pictures comes in one update, and a roof's cap takes a while */
    budgetMs: 5,
    /** a bake that fails is tried again this many times before its picture is left bare for the match */
    tries: 3
});

export type CapKind = "roof" | "thing";

export const smooth = (a: number, b: number, x: number): number => {
    const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
    return t * t * (3 - 2 * t);
};

/** a box blur of a grid of values, `radius` cells each way */
export function boxBlur(values: Float32Array, w: number, h: number, radius: number): Float32Array {
    const pass = new Float32Array(w * h);
    const out = new Float32Array(w * h);
    const span = radius * 2 + 1;
    for (let y = 0; y < h; y++) {
        let sum = 0;
        for (let x = -radius; x <= radius; x++) sum += values[y * w + Math.min(w - 1, Math.max(0, x))];
        for (let x = 0; x < w; x++) {
            pass[y * w + x] = sum / span;
            const leave = Math.max(0, x - radius), enter = Math.min(w - 1, x + radius + 1);
            sum += values[y * w + enter] - values[y * w + leave];
        }
    }
    for (let x = 0; x < w; x++) {
        let sum = 0;
        for (let y = -radius; y <= radius; y++) sum += pass[Math.min(h - 1, Math.max(0, y)) * w + x];
        for (let y = 0; y < h; y++) {
            out[y * w + x] = sum / span;
            const leave = Math.max(0, y - radius), enter = Math.min(h - 1, y + radius + 1);
            sum += pass[enter * w + x] - pass[leave * w + x];
        }
    }
    return out;
}

/** the longest side a cap of `kind` is baked at (pixels) */
export const capSize = (kind: CapKind): number => kind === "roof" ? CAP.roofSize : CAP.size;

/**
 * the cap's pixels (RGBA, `w` by `h`) from its picture's (straight RGBA as read off the atlas page, the same size): its
 * alpha eroded (two box blurs), the snow kept where it's still nearly whole, under patches laid by two smooth grains of
 * noise (each match its own, by `seed`); none when no snow lies on it
 */
export function snowCapPixels(pixels: Uint8ClampedArray, w: number, h: number, kind: CapKind, seed: number): Uint8ClampedArray | undefined {
    const size = capSize(kind);
    const alpha = new Float32Array(w * h);
    for (let i = 0; i < w * h; i++) alpha[i] = pixels[i * 4 + 3] / 255;
    // eroded: the alpha blurred (two box blurs), the snow kept where it's still nearly whole
    const radius = Math.max(1, Math.round(CAP.erode * Math.max(w, h)));
    const blurred = boxBlur(boxBlur(alpha, w, h, radius), w, h, radius);
    const out = new Uint8ClampedArray(w * h * 4);
    // (the patches laid by two smooth grains of noise, big ones and smaller ones in them, each cap its own by the seed;
    // a roof's in its own measure: big drifts, and more of them)
    const ox = seed * 0.173 + (kind === "roof" ? 0.5 : 0), oy = seed * 0.091;
    const across = Math.max(w, h) / size;
    const scale1 = (kind === "roof" ? 1.1 : 1.7) * across, scale2 = scale1 * 2.6;
    const heavy = kind === "roof" ? 0.07 : 0;
    const noise = (u: number, v: number): number => 0.7 * sampleNoise(u * scale1 + ox, v * scale1 + oy, 0) + 0.3 * sampleNoise(u * scale2 + ox * 2.1, v * scale2 + oy * 2.1, 3);
    let any = false;
    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            const i = y * w + x;
            const inner = smooth(0.55, 0.9, blurred[i]);
            if (inner <= 0.002) continue;
            const u = x / w, v = y / h;
            const n = noise(u, v);
            const cover = smooth(CAP.cover.from - heavy, CAP.cover.to - heavy, n);
            if (cover <= 0.002) continue;
            // (its own shading: the patch's top left lit, its bottom right in a blue shadow)
            const slope = Math.max(-1, Math.min(1, (n - noise(u + 0.015, v + 0.015)) * 16));
            const light = 0.86 + 0.14 * (0.5 - v * 0.5 - u * 0.3) + slope * 0.1;
            const r = 238 * light, g = 243 * light, b = 250 * Math.min(1, light + 0.04);
            const o = i * 4;
            out[o] = Math.max(0, Math.min(255, r));
            out[o + 1] = Math.max(0, Math.min(255, g));
            out[o + 2] = Math.max(0, Math.min(255, b));
            out[o + 3] = Math.round(255 * CAP.alpha * inner * cover);
            any = true;
        }
    }
    return any ? out : undefined;
}
